import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Playground = () => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkPlaygroundStatus();
  }, []);

  const checkPlaygroundStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/playground/status`);
      setIsActive(response.data.active);
      
      if (!response.data.active) {
        // Redirect to home if playground is not active
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (error) {
      console.error('Error checking playground status:', error);
      setIsActive(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Playground is Not Active</h2>
              <p className="text-gray-600 mb-4">
                The playground is currently disabled. Please contact the administrator.
              </p>
              <p className="text-sm text-gray-500">Redirecting to home page...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Playground</h1>
            <button
              onClick={() => navigate('/')}
              className="text-orange-600 hover:text-orange-700 font-medium"
            >
              Go to Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="doors" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="doors">Doors and Frames</TabsTrigger>
            <TabsTrigger value="windows">Windows</TabsTrigger>
            <TabsTrigger value="curtainwalls">Curtain Walls</TabsTrigger>
            <TabsTrigger value="storefronts">Storefronts</TabsTrigger>
          </TabsList>

          {/* Doors and Frames Tab */}
          <TabsContent value="doors">
            <Tabs defaultValue="door-types">
              <TabsList className="mb-4">
                <TabsTrigger value="door-types">Door Types</TabsTrigger>
                <TabsTrigger value="hardware">Hardware</TabsTrigger>
                <TabsTrigger value="door-list">Door List</TabsTrigger>
                <TabsTrigger value="elevations">Elevations</TabsTrigger>
              </TabsList>

              <TabsContent value="door-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Door Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Door types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="hardware">
                <Card>
                  <CardHeader>
                    <CardTitle>Hardware</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Hardware content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="door-list">
                <Card>
                  <CardHeader>
                    <CardTitle>Door List</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Door list content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="elevations">
                <Card>
                  <CardHeader>
                    <CardTitle>Elevations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Elevations content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Windows Tab */}
          <TabsContent value="windows">
            <Tabs defaultValue="types">
              <TabsList className="mb-4">
                <TabsTrigger value="types">Window Types</TabsTrigger>
                <TabsTrigger value="glass-types">Glass Types</TabsTrigger>
                <TabsTrigger value="list">Window List</TabsTrigger>
                <TabsTrigger value="elevations">Elevations</TabsTrigger>
              </TabsList>

              <TabsContent value="types">
                <Card>
                  <CardHeader>
                    <CardTitle>Window Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Window types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="glass-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Glass Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Glass types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="list">
                <Card>
                  <CardHeader>
                    <CardTitle>Window List</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Window list content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="elevations">
                <Card>
                  <CardHeader>
                    <CardTitle>Elevations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Elevations content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Curtain Walls Tab */}
          <TabsContent value="curtainwalls">
            <Tabs defaultValue="cw-types">
              <TabsList className="mb-4">
                <TabsTrigger value="cw-types">CW Types</TabsTrigger>
                <TabsTrigger value="glass-types">Glass Types</TabsTrigger>
                <TabsTrigger value="metal-types">Metal Panel Types</TabsTrigger>
                <TabsTrigger value="panel-list">Panel List</TabsTrigger>
              </TabsList>

              <TabsContent value="cw-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Curtainwall Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Curtainwall types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="glass-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Glass Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Glass types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="metal-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Metal Panel Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Metal panel types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="panel-list">
                <Card>
                  <CardHeader>
                    <CardTitle>Panel List</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Panel list content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Storefronts Tab */}
          <TabsContent value="storefronts">
            <Tabs defaultValue="sf-types">
              <TabsList className="mb-4">
                <TabsTrigger value="sf-types">SF Types</TabsTrigger>
                <TabsTrigger value="glass-types">Glass Types</TabsTrigger>
                <TabsTrigger value="metal-types">Metal Panel Types</TabsTrigger>
                <TabsTrigger value="panel-list">Panel List</TabsTrigger>
              </TabsList>

              <TabsContent value="sf-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Storefront Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Storefront types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="glass-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Glass Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Glass types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="metal-types">
                <Card>
                  <CardHeader>
                    <CardTitle>Metal Panel Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Metal panel types content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="panel-list">
                <Card>
                  <CardHeader>
                    <CardTitle>Panel List</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">Panel list content will be displayed here.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Playground;
